import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const STEPS = [
  {
    step: "01",
    title: "Tell us your brief",
    desc: "Select the services you need and share your objectives, location and timeline.",
  },
  {
    step: "02",
    title: "Set your budget",
    desc: "Provide a separate indicative budget for each selected activity.",
  },
  {
    step: "03",
    title: "We review",
    desc: "We assess your requirements and identify the relevant service and delivery needs.",
  },
  {
    step: "04",
    title: "Discuss a proposal",
    desc: "We follow up to clarify details and discuss a suitable scope and proposal.",
  },
];

export default function ProcessSection() {
  return (
    <section id="process" className="relative py-20 sm:py-28 scroll-mt-20 border-t border-black/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.3em] uppercase text-gold-dark"
          >
            How we work
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight text-royal-deep leading-[1.1]"
          >
            A simple process. <span className="text-stroke">A clear next step.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-5 text-sm sm:text-lg text-black/70 leading-relaxed"
          >
            Share your requirement and indicative budget. Our team can then review the brief and
            discuss scope, timelines and the next steps with you.
          </motion.p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.step}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid="process-step"
              className="group relative rounded-3xl border border-black/10 bg-white p-7 overflow-hidden transition-all duration-300 hover:border-gold/50 hover:shadow-[0_14px_40px_rgba(224,19,39,0.12)]"
            >
              <span className="font-display text-5xl font-bold text-black/10 transition-colors duration-300 group-hover:text-gold/40">
                {s.step}
              </span>
              <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-royal-deep">
                {s.title}
              </h3>
              <p className="mt-2.5 text-sm text-black/65 leading-relaxed">{s.desc}</p>
              {i < STEPS.length - 1 && (
                <ArrowRight
                  size={16}
                  className="hidden lg:block absolute top-9 right-6 text-black/25 group-hover:text-gold transition-colors duration-300"
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
